// src/components/demo/DemoCheckIn.js
//
// Check-in confirm + success (design handoff §03-16/17). Condition is two
// side-by-side chips (OK = teal/lagoon, Damage = quiet) — off the main path.
// Success = TEAL ground ("back on the rack / complete" semantic), confirms
// the ride-summary email is on its way, 72px scan-next CTA.

import React, { useState } from 'react';
import { FScreen, FHeader, FHeadline, FBtn, FLabel, FWordmark, FBoardCard } from '../foam/FoamPrimitives';

function durationLabel(checkedOutAt) {
  const t = checkedOutAt?.toDate ? checkedOutAt.toDate() : null;
  if (!t) return '—';
  const mins = Math.max(0, Math.round((Date.now() - t.getTime()) / 60000));
  return mins >= 60 ? `${Math.floor(mins / 60)}h ${mins % 60}m` : `${mins} min`;
}

const Row = ({ k, v }) => (
  <div
    className="flex justify-between items-center"
    style={{ padding: '14px 0', borderBottom: '1px solid var(--foam-hair)' }}
  >
    <FLabel>{k}</FLabel>
    <span className="font-sans font-semibold text-[15px]">{v}</span>
  </div>
);

// Check-in and void are transactions now (demoService §4.2) — they refuse if
// the session was already closed on another phone, or if there is no signal.
// `catch { setBusy(false); }` showed staff nothing at all.
const FailPanel = ({ title, message }) => (
  <div style={{ border: '1.5px solid var(--foam-orange)', background: '#FFF7F3', padding: 16 }}>
    <div
      className="font-display font-extrabold uppercase text-[18px]"
      style={{ color: 'var(--foam-orange)', letterSpacing: '-0.01em' }}
    >
      {title}
    </div>
    <p className="font-sans text-[14px] mt-[6px]" style={{ color: 'var(--foam-ink)', lineHeight: 1.5 }}>
      {message}
    </p>
  </div>
);

export default function DemoCheckIn({ product, session, onConfirm, onVoid, onBack, done }) {
  const [busy, setBusy] = useState(false);
  const [damage, setDamage] = useState(false);
  const [note, setNote] = useState('');
  const [checkInNote, setCheckInNote] = useState('');
  const [error, setError] = useState(null);       // { title, message }

  if (done) {
    return (
      <div
        className="w-full mx-auto max-w-[430px] flex flex-col"
        style={{
          height: '100dvh', background: 'var(--foam-teal)', color: 'var(--foam-paper)',
          fontFamily: '"Archivo", -apple-system, system-ui, sans-serif', padding: '56px 22px 30px',
        }}
      >
        <div
          className="flex justify-between items-center pb-[14px]"
          style={{ borderBottom: '1.5px solid var(--foam-teal-bright, #5FBFB2)' }}
        >
          <FWordmark dark />
          <FLabel color="var(--foam-lagoon, #CDE7E0)">● Back on the rack</FLabel>
        </div>
        <div className="flex-1 flex flex-col justify-center gap-[18px]">
          <div
            className="flex items-center justify-center"
            style={{ width: 56, height: 56, background: 'var(--foam-paper)', color: 'var(--foam-teal)', fontSize: 30 }}
          >
            ✓
          </div>
          <FHeadline size={60} color="var(--foam-paper)">Checked<br />in.</FHeadline>
          <p className="font-sans text-[16px]" style={{ color: 'var(--foam-lagoon, #CDE7E0)', maxWidth: 300 }}>
            {damage
              ? `${product.name} is benched for maintenance — clear it from the board card when it's ready. `
              : `${product.name} is available again. `}
            {session.riderFirstName}'s ride-summary email is on its way — the exact board they rode, with a
            link to learn more.
          </p>
        </div>
        <FBtn primary height={72} className="!text-[18px]" onClick={onBack}>Scan next board</FBtn>
      </div>
    );
  }

  return (
    <FScreen
      header={<FHeader right={<FLabel>Scanned</FLabel>} />}
      footer={
        <>
          <FBtn
            primary
            disabled={busy}
            onClick={async () => {
              setBusy(true);
              setError(null);
              try {
                await onConfirm({
                  condition: damage ? 'damage' : 'ok',
                  conditionNote: damage ? (note || null) : null,
                  checkInNote: checkInNote || null,
                });
              } catch (e) {
                setError({
                  title: "Couldn't check in",
                  message: e?.message || 'The check-in did not go through. Check the signal and try again.',
                });
                setBusy(false);
              }
            }}
          >
            {busy ? 'Checking in…' : 'Check in'}
          </FBtn>
          <FBtn ghost disabled={busy} onClick={onBack}>Back</FBtn>
        </>
      }
    >
      <div className="flex justify-between items-center">
        <FLabel>This board</FLabel>
        <span
          className="font-mono text-[12px] uppercase tracking-[0.1em] px-[10px] py-[5px]"
          style={{ background: 'var(--foam-orange)', color: 'var(--foam-carbon)' }}
        >
          ● Out · {durationLabel(session.checkedOutAt)}
        </span>
      </div>
      {error && <FailPanel title={error.title} message={error.message} />}
      <FBoardCard product={product} />
      <div>
        <Row k="Rider" v={`${session.riderFirstName} ${session.riderLastName}`} />
        <Row k="Out for" v={durationLabel(session.checkedOutAt)} />
      </div>

      {/* Condition chips — quiet secondary (design: DCheckIn) */}
      <div className="flex gap-[10px]">
        <button
          type="button"
          onClick={() => setDamage(false)}
          className="flex-1 font-sans font-bold text-[13px] uppercase text-center"
          style={{
            minHeight: 48, letterSpacing: '0.08em',
            border: `1.5px solid ${!damage ? 'var(--foam-teal)' : 'var(--foam-hair)'}`,
            background: !damage ? 'var(--foam-lagoon, #CDE7E0)' : 'transparent',
            color: !damage ? 'var(--foam-teal)' : 'var(--foam-ink-2)',
          }}
        >
          Condition OK
        </button>
        <button
          type="button"
          onClick={() => setDamage(true)}
          className="flex-1 font-sans font-bold text-[13px] uppercase text-center"
          style={{
            minHeight: 48, letterSpacing: '0.08em',
            border: `1.5px solid ${damage ? 'var(--foam-orange)' : 'var(--foam-hair)'}`,
            background: damage ? '#FFF7F3' : 'transparent',
            color: damage ? 'var(--foam-orange)' : 'var(--foam-ink-2)',
          }}
        >
          Damage — note
        </button>
      </div>
      {damage && (
        <input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="What happened? (optional)"
          className="w-full font-sans text-[16px] px-[14px]"
          style={{ height: 56, border: '1.5px solid var(--foam-ink)', borderRadius: 0, background: '#FFFFFF' }}
        />
      )}

      {/* General notes — optional, for the demo-day report (rider feedback,
          fin swaps, sizing observations…) */}
      <input
        value={checkInNote}
        onChange={(e) => setCheckInNote(e.target.value)}
        placeholder="Notes — optional (rider feedback, setup, …)"
        className="w-full font-sans text-[16px] px-[14px]"
        style={{ height: 56, border: '1.5px solid var(--foam-hair)', borderRadius: 0, background: '#FFFFFF' }}
      />

      <button
        type="button"
        onClick={async () => {
          // eslint-disable-next-line no-alert
          const reason = window.prompt('Void this session? Reason:');
          if (reason === null) return;
          setBusy(true);
          setError(null);
          try {
            await onVoid(reason);
          } catch (e) {
            setError({
              title: "Couldn't void",
              message: e?.message || 'The void did not go through. Check the signal and try again.',
            });
            setBusy(false);
          }
        }}
        className="font-mono text-[12px] tracking-[0.14em] uppercase underline text-left"
        style={{ color: 'var(--foam-mute)', minHeight: 44 }}
      >
        Wrong rider / board — void session
      </button>
    </FScreen>
  );
}
